import SliderArrowSvg from '@public/icons/btn-arr.svg';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { HTMLAttributes, MouseEvent } from 'react';
import Link from '@/shared/components/shared/Link';
import { encodeUrl } from '@/shared/lib/utils/filter/chpu/encode-url';
import { generatePagination } from '@/shared/lib/utils/generate-pagination';

type Props = HTMLAttributes<HTMLElement> & {
    /**
     * Кол-во страниц
     */
    totalPages: number;
    /**
     * текущая страница
     */
    activePage: number;
    /**
     * ЧПУ: игнорируемые ключи
     */
    chpuIgnoredKeys?: string[];

    onClickHandler?: (pageTrigger: number) => void;
    onClickPreventDefault?: boolean;
};

const ListPagination = ({
    totalPages,
    activePage,
    onClickHandler,
    onClickPreventDefault = false,
    chpuIgnoredKeys,
}: Props) => {
    const pagesToShow = generatePagination(totalPages, activePage);
    const router = useRouter();
    const calculatedAsPath = router.asPath;

    const clickHandler = (e: MouseEvent<HTMLButtonElement>, pageTrigger: number) => {
        if (onClickPreventDefault) {
            e.preventDefault();
        }

        onClickHandler?.(pageTrigger);
    };

    return (
        <div className="pagination-list text-default-medium">
            <Link
                href={`${encodeUrl(calculatedAsPath, { ...router.query, page: Math.max(1, activePage - 1) }, { ignoredKeys: chpuIgnoredKeys })}`}
                className={classNames('pagination-list-ctrl pagination-list-ctrl--prev', {
                    'is-disabled': activePage === 1,
                })}
                onClick={(e: MouseEvent<HTMLButtonElement> & MouseEvent<HTMLAnchorElement>) =>
                    clickHandler(e, Math.max(1, activePage - 1))
                }
                aria-label="Предыдущая страница"
            >
                <SliderArrowSvg />
            </Link>
            {pagesToShow.map((item) =>
                item.ellipsis ? (
                    <Link
                        key={item.targetPage}
                        href={`${encodeUrl(calculatedAsPath, { ...router.query, page: item.targetPage }, { ignoredKeys: chpuIgnoredKeys })}`}
                        className="pagination-list-link text-xs"
                        aria-label={`№ ${item.targetPage}`}
                        onClick={(e: MouseEvent<HTMLButtonElement> & MouseEvent<HTMLAnchorElement>) =>
                            clickHandler(e, item.targetPage)
                        }
                    >
                        ...
                    </Link>
                ) : (
                    <Link
                        key={item.targetPage}
                        href={`${encodeUrl(calculatedAsPath, { ...router.query, page: item.targetPage }, { ignoredKeys: chpuIgnoredKeys })}`}
                        className={classNames('pagination-list-link text-xs', {
                            'pagination-list-link--active': item.targetPage == activePage,
                        })}
                        tabIndex={item.targetPage == activePage ? -1 : 0}
                        onClick={(e: MouseEvent<HTMLButtonElement> & MouseEvent<HTMLAnchorElement>) =>
                            clickHandler(e, item.targetPage)
                        }
                    >
                        {item.targetPage}
                    </Link>
                ),
            )}
            <Link
                href={`${encodeUrl(calculatedAsPath, { ...router.query, page: Math.min(totalPages, activePage + 1) }, { ignoredKeys: chpuIgnoredKeys })}`}
                className={classNames('pagination-list-ctrl pagination-list-ctrl--next', {
                    'is-disabled': activePage == totalPages,
                })}
                onClick={(e: MouseEvent<HTMLButtonElement> & MouseEvent<HTMLAnchorElement>) =>
                    clickHandler(e, Math.min(totalPages, activePage + 1))
                }
                aria-label="Следующая страница"
            >
                <SliderArrowSvg />
            </Link>
        </div>
    );
};

export default ListPagination;
